import { retrieveFeatureLibrary } from '@features/featureLibrary/featureLibrarySlice';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentClient } from '../../features/client/clientSlice';
import { selectSessionStatus } from '../../features/session/sessionSlice';

// @mui/material components
import Avatar from '@mui/material/Avatar';
import { makeStyles } from '@mui/styles';

// @mui/icons-material
import AddIcon from '@mui/icons-material/Add';
import Workstation from '@mui/icons-material/Computer';
import Environment from '@mui/icons-material/SettingsInputComponent';
import Location from '@mui/icons-material/Store';

// core components
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Info from 'components/Typography/Info.js';

import {
  useGetClientsQuery,
  useGetClientStatsQuery,
  useLazyGetClientsQuery,
  useLazyGetClientTenantsQuery,
} from 'api/client.api';
import styles from 'assets/jss/material-dashboard-pro-react/views/clientStyle.js';

const useStyles = makeStyles(styles);

const isDateToday = (date) => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const day = today.getDay();
  const aDate = new Date(date);
  if (
    aDate.getFullYear() === year &&
    aDate.getMonth() === month &&
    aDate.getDay() === day
  )
    return true;
  else return false;
};

export default function Clients(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const sessionStatus = useSelector(selectSessionStatus);
  const { data: clients } = useGetClientsQuery();
  const { data: clientStats } = useGetClientStatsQuery();
  const [triggerGetClients] = useLazyGetClientsQuery();
  const [triggerGetClientTenants] = useLazyGetClientTenantsQuery();

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (sessionStatus === 'active' && !initialized) {
      setInitialized(true);
    }
  }, [sessionStatus, initialized]);

  useEffect(() => {
    if (
      sessionStatus === 'active' &&
      initialized &&
      (clients === null || clients.length === 0)
    ) {
      triggerGetClients().unwrap();
    }
  }, [sessionStatus, initialized, clients]);

  const addClientHandler = (history) => {
    history.push('/admin/provisionclient');
  };

  const selectClientHandler = async (history, client) => {
    await dispatch(setCurrentClient(client));
    await triggerGetClientTenants(client._id).unwrap();
    await dispatch(retrieveFeatureLibrary(client._id));
    history.push('/admin/client', client);
  };

  const emailContact = (emailAddress) => {
    window.location.href = `mailto:${emailAddress}`;
  };

  const environmentCount = (clientId) => {
    var count = 0;
    if (clientStats) {
      const clientEntry = clientStats.tenants.find(
        (client) => client._id === clientId
      );
      if (clientEntry) count = clientEntry.count;
    }
    return count;
  };
  const locationCount = (clientId) => {
    var count = 0;
    if (clientStats) {
      const clientEntry = clientStats.locations.find(
        (client) => client._id === clientId
      );
      if (clientEntry) count = clientEntry.count;
    }
    return count;
  };
  const workstationCount = (clientId) => {
    var count = 0;
    if (clientStats) {
      const clientEntry = clientStats.workstations.find(
        (client) => client._id === clientId
      );
      if (clientEntry) count = clientEntry.count;
    }
    return count;
  };

  const buildClients = () => {
    const clientCard = clients.map((client, index) => (
      <GridItem xs={12} sm={6} md={6} lg={3} key={index}>
        <Card style={{ height: '100%' }}>
          <CardHeader
            color='primary'
            stats
            icon
            onClick={(history) => selectClientHandler(props.history, client)}
          >
            <CardIcon color='primary'>
              <Avatar src={client.logo} variant='square' />
            </CardIcon>
            <Info className={classes.cardCategory}>
              {isDateToday(client.created_at)
                ? 'New'
                : client.status === 'active'
                ? 'Active'
                : 'Inactive'}
            </Info>
            <h3 className={classes.cardTitle}>{client.name}</h3>
          </CardHeader>
          <CardBody style={{ alignContent: 'end', height: 60 }}>
            <GridContainer>
              <GridItem xs={2}>
                <Environment />
              </GridItem>
              <GridItem xs={7}>Environments:</GridItem>
              <GridItem xs={3} className={classes.tenantStats}>
                {environmentCount(client._id)}
              </GridItem>
              <GridItem xs={2}>
                <Location />
              </GridItem>
              <GridItem xs={7}>Locations:</GridItem>
              <GridItem xs={3} className={classes.tenantStats}>
                {locationCount(client._id)}
              </GridItem>
              <GridItem xs={2}>
                <Workstation />
              </GridItem>
              <GridItem xs={7}>Workstations:</GridItem>
              <GridItem xs={3} className={classes.tenantStats}>
                {workstationCount(client._id)}
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </GridItem>
    ));

    return clientCard;
  };

  return (
    <div>
      <GridContainer spacing={5}>
        <GridItem xs={12} sm={10} />
        <GridItem xs={12} sm={2}>
          <Button
            color='info'
            className={classes.marginRight}
            onClick={(history) => addClientHandler(props.history)}
          >
            <AddIcon className={classes.icons} />
            Add New Company
          </Button>
        </GridItem>
      </GridContainer>
      <GridContainer sx={{ gap: '3rem 0' }}>
        {clients !== null ? buildClients() : null}
      </GridContainer>
    </div>
  );
}
